/**
 * useRoutineChat — the RoutineBuilder chat session for the split-view editor (P12 §6.4, T-FE3).
 *
 * A near-verbatim clone of `useDashboardChat` (the only other scoped-agent chat surface) adapted for
 * the RoutineBuilder. It owns the hard-won race handling — optimistic user_message, the
 * `activeTurnId` bridge, `filterOutTurn`, database-confirmed Stop, connection-lost fallback, Strict-Mode
 * recovery — and wires it to the shared {@link RoutineDraftStore}:
 *
 *   - On each turn it sends `routineDraft: store.snapshot()` (snapshot-at-turn-start, §3.4) so the
 *     agent sees the live on-screen draft.
 *   - Incoming ephemeral `routine_draft` SSE events are routed to `store.applyAgentPatch(patch)` in
 *     the `onEvent` callback (the same mechanism `useDashboardChat` uses for `turn_complete`), so the
 *     editor fills in live, mid-turn. A per-turn-monotonic `patchSeq` guard drops out-of-order /
 *     duplicate patches.
 *
 * Differences from the dashboard hook:
 *   - The panel is always present (no open/close toggle) — the chat is one of the two split panes.
 *   - The session is keyed on a STABLE id: the saved `workflowId` when editing, or a client-minted
 *     `draftLocalId` (uuid) for a brand-new routine, so the session survives new→saved without
 *     re-keying (§4.5). Callers pass this as `sessionKey`.
 *   - The RPC namespace is `routineChat` (`getOrCreate`/`sendMessage`/`stop`/`reset`).
 */
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { SSEMessage } from '@/_shared/types';

import { useEndedStreamHistory } from '../../chat/hooks/useEndedStreamHistory';
import { useSSE } from '../../chat/hooks/useSSE';
import { useTurnStop } from '../../chat/hooks/useTurnStop';
import { filterOutTurn, isTurnTerminalInHistory } from '../../chat/lib/turn-slicing';
import type { RoutineDraftStore } from './useRoutineDraft';

export interface UseRoutineChatReturn {
  sessionId: string | null;
  messages: SSEMessage[];
  connected: boolean;
  error: string | null;
  sendMessage: (content: string) => void;
  resetChat: () => void;
  agentActive: boolean;
  stopping: boolean;
  stopStatusError: boolean;
  stopStalled: boolean;
  retryStopStatus: () => void;
  /** True when the session has been created/loaded and messages can be sent. */
  sessionReady: boolean;
  /** Fire the abort for the active turn. No-op when no turn is active. */
  stopTurn: () => void;
  /** True when the agent is active AND a turn ID is confirmed — safe to show the Stop button. */
  canStop: boolean;
  /** A message to put back into the composer: one whose send failed. A stopped prompt stays in the transcript. */
  prefillValue: string | undefined;
  /** Bumps each time a new prefill should be applied. */
  prefillNonce: number;
}

export function useRoutineChat(store: RoutineDraftStore, sessionKey: string): UseRoutineChatReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentActive, setAgentActive] = useState(false);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [adoptedRuntimeTurn, setAdoptedRuntimeTurn] = useState<{ sessionId: string; turnId: string } | null>(null);
  const [prefillValue, setPrefillValue] = useState<string | undefined>(undefined);
  const [prefillNonce, setPrefillNonce] = useState(0);
  const { slug } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const initializedForSession = useRef<string | null>(null);
  const turnStop = useTurnStop({
    workspaceKey: slug,
    sessionId,
    activeTurnId,
    stopCall: (input) => orpc.routineChat.stop.call(input),
    // A stopped prompt stays in the transcript, marked "Stopped" (the abort marker), as in every other
    // chat surface. The composer is never touched.
    onConfirmed: () => {
      setAgentActive(false);
      setActiveTurnId(null);
    },
  });
  const { noteTerminal, noteStreamEnded, stop, blockingTurnId } = turnStop;

  // Reset all session state when the routine changes (render-phase pattern).
  const [prevSessionKey, setPrevSessionKey] = useState(sessionKey);
  if (sessionKey !== prevSessionKey) {
    setPrevSessionKey(sessionKey);
    setSessionId(null);
    setAgentActive(false);
    setActiveTurnId(null);
    setAdoptedRuntimeTurn(null);
    setPrefillValue(undefined);
    setPrefillNonce(0);
  }

  // getOrCreate the RoutineBuilder session — eager (the chat pane is always mounted).
  const { data: sessionData, refetch: refetchSession } = useQuery(
    orpc.routineChat.getOrCreate.queryOptions({
      input: sessionKey ? { workflowId: sessionKey } : skipToken,
    }),
  );

  // Sync session ID from server data (render-phase pattern).
  // Seeds to null, NOT to serverSessionId: this tracks which server value we've already adopted, and
  // at mount we've adopted none. Seeding it to the current value means a getOrCreate that resolves
  // from the TanStack cache — i.e. any re-entry to a routine already visited this page-load — is
  // present on the FIRST render, registers as "already adopted", and never reaches setSessionId,
  // stranding the chat on "Getting ready…" until a reload clears the cache (AI-1187).
  const serverSessionId = sessionData?.session?.id ?? null;
  const [prevServerSessionId, setPrevServerSessionId] = useState<string | null>(null);
  if (serverSessionId !== prevServerSessionId) {
    setPrevServerSessionId(serverSessionId);
    if (serverSessionId && serverSessionId !== sessionId) {
      setSessionId(serverSessionId);
    }
  }

  // ── Patch ordering guard (§6.3) ──
  // Each `routine_draft` patch carries a per-turn-monotonic `patchSeq`. Drop patches with
  // `patchSeq <= lastApplied` (idempotent, monotone) so out-of-order delivery never regresses the
  // editor. Resets per turn; cross-turn order is guaranteed by server-side turn serialization.
  const lastPatchSeqRef = useRef<number>(-1);
  const lastPatchTurnRef = useRef<string | null>(null);

  // SSE event callback — routes the ephemeral `routine_draft` patch into the draft store and clears
  // turn state on terminal events. Runs in the EventSource listener (not a React effect).
  const handleSSEEvent = useCallback(
    (eventType: string, data: Record<string, unknown>) => {
      if (eventType === 'turn_complete' || eventType === 'turn_aborted' || eventType === 'error') {
        const id =
          typeof data['turnId'] === 'string'
            ? data['turnId']
            : (activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId);
        if (id) noteTerminal(id);
      }
      if (eventType === 'routine_draft') {
        const turnId = typeof data['turnId'] === 'string' ? data['turnId'] : null;
        const patchSeq = typeof data['patchSeq'] === 'number' ? data['patchSeq'] : null;
        const patch = data['patch'];
        if (!patch || typeof patch !== 'object') return;
        // Reset the monotone guard on a new turn.
        if (turnId !== lastPatchTurnRef.current) {
          lastPatchTurnRef.current = turnId;
          lastPatchSeqRef.current = -1;
        }
        if (patchSeq !== null && patchSeq <= lastPatchSeqRef.current) return; // stale / duplicate
        if (patchSeq !== null) lastPatchSeqRef.current = patchSeq;
        store.applyAgentPatch(patch as Parameters<RoutineDraftStore['applyAgentPatch']>[0]);
        return;
      }
      if (eventType === 'turn_complete' || eventType === 'error') {
        setAgentActive(false);
        setActiveTurnId(null);
        // Refetch the workflow so agent-driven status/field changes (set_routine_active, save_workflow)
        // are reflected live without a manual reload. Safe: useRoutineDraft's loadedId guard keys on
        // wf.id, so the refetch updates workflowStatus without re-seeding the on-screen draft.
        if (store.workflowId) {
          void queryClient.invalidateQueries({
            queryKey: orpc.workflow.get.queryOptions({ input: { id: store.workflowId } }).queryKey,
          });
        }
      }
      if (eventType === 'turn_aborted') {
        setAgentActive(false);
        setActiveTurnId(null);
        if (store.workflowId) {
          void queryClient.invalidateQueries({
            queryKey: orpc.workflow.get.queryOptions({ input: { id: store.workflowId } }).queryKey,
          });
        }
      }
    },
    [store, queryClient, orpc, activeTurnId, sessionData?.runtime?.activeTurn?.turnId, noteTerminal],
  );

  // Don't clear agentActive on disconnect — the SSE will retry and reconnect. If the turn completed
  // during the gap, the replayed turn_complete/error event clears it. Clearing here would falsely
  // unlock the UI while the agent is still running server-side.
  const handleSSEDisconnect = useCallback(() => {
    // intentionally empty — agentActive is managed by SSE events, not connectivity
  }, []);

  const sseOptions = useMemo(
    () => ({ onEvent: handleSSEEvent, onDisconnect: handleSSEDisconnect }),
    [handleSSEEvent, handleSSEDisconnect],
  );

  // SSE connection — turn-scoped: only connect when there's an active turn to follow.
  const knownTurnId = activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId ?? null;
  const streamUrl =
    sessionId && knownTurnId && slug
      ? `/api/w/${encodeURIComponent(slug)}/chats/${sessionId}/turns/${knownTurnId}/events`
      : null;
  const {
    messages,
    connected,
    error: sseError,
    ended: sseEnded,
    endedTurnId,
    recovered: streamHistoryRecovered,
    recoverHistory,
    addMessage,
    removeLastUserMessage,
    initializeMessages,
    clearMessages,
  } = useSSE(streamUrl, {
    ...sseOptions,
    scopeKey: sessionId,
    turnId: knownTurnId,
    recovery: {
      sessionId,
      persistedMessages: sessionData?.sseMessages,
      runtimeTurnId: sessionData?.runtime?.activeTurn?.turnId ?? null,
      refetchSession,
      controlsEnabled: false,
    },
  });
  // Start reading saved events on a failed connection attempt, rather than waiting for all SSE
  // retries. The turn-specific read can confirm completion even if turn_start never reached us.
  const disconnectedTurnId =
    agentActive && activeTurnId && !connected && sseError?.startsWith('Reconnecting') ? activeTurnId : null;
  useEndedStreamHistory({
    sessionId,
    turnId: sseEnded ? endedTurnId : disconnectedTurnId,
    ended: !streamHistoryRecovered && (sseEnded || disconnectedTurnId !== null),
    recoverHistory,
  });

  const reportedTurnId = sessionData?.runtime?.activeTurn?.turnId ?? null;
  if (
    sessionId &&
    sessionData?.session.id === sessionId &&
    reportedTurnId &&
    (adoptedRuntimeTurn?.sessionId !== sessionId || adoptedRuntimeTurn.turnId !== reportedTurnId) &&
    (!activeTurnId || activeTurnId === reportedTurnId)
  ) {
    setAdoptedRuntimeTurn({ sessionId, turnId: reportedTurnId });
    if (
      !isTurnTerminalInHistory(sessionData.sseMessages, reportedTurnId) &&
      !isTurnTerminalInHistory(messages, reportedTurnId)
    ) {
      setAgentActive(true);
      setActiveTurnId(reportedTurnId);
    }
  }

  useEffect(() => {
    if (sseEnded && knownTurnId) noteStreamEnded(knownTurnId);
  }, [sseEnded, knownTurnId, noteStreamEnded]);

  // Clear the local stream marker after a final transport failure. The shared Stop hook
  // keeps Send blocked until a database read confirms the turn is terminal.
  useEffect(() => {
    if (agentActive && activeTurnId && sseEnded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async stream failure unlocks the local turn
      setAgentActive(false);
      setActiveTurnId(null);
    }
  }, [agentActive, activeTurnId, sseEnded]);

  // Clear messages when sessionId resets (routine switch).
  useEffect(() => {
    if (!sessionId) clearMessages();
  }, [sessionId, clearMessages]);

  // Initialize from historical messages (once per session). Filter out events belonging to the
  // active turn — the SSE stream owns those. Race-safe: if SSE delivered events before sessionData
  // arrived, prepend historical messages instead of replacing.
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  });

  useEffect(() => {
    if (sessionId && sessionData?.sseMessages && initializedForSession.current !== sessionId) {
      initializedForSession.current = sessionId;
      const filtered = knownTurnId ? filterOutTurn(sessionData.sseMessages, knownTurnId) : sessionData.sseMessages;
      const currentSSE = messagesRef.current;
      if (currentSSE.length > 0) {
        initializeMessages([...filtered, ...currentSSE]);
      } else {
        initializeMessages(filtered);
      }
    }
  }, [sessionId, sessionData?.sseMessages, knownTurnId, initializeMessages]);

  const stopTurn = useCallback(() => {
    const id = knownTurnId ?? blockingTurnId;
    if (id) stop(id);
  }, [knownTurnId, blockingTurnId, stop]);

  // ── Send message ──
  const sendMessageMutation = useMutation(orpc.routineChat.sendMessage.mutationOptions());

  const sendMessage = useCallback(
    (content: string) => {
      if (!sessionId || turnStop.blocking) return;
      // Snapshot-at-turn-start (§3.4): the live on-screen draft, persisted vocab.
      const routineDraft = store.snapshot();

      addMessage({ type: 'user_message', content });
      setAgentActive(true);

      const variables = {
        sessionId,
        content,
        routineDraft,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        clientRequestId: crypto.randomUUID(),
      };
      sendMessageMutation.mutate(variables, {
        onSuccess: (data) => {
          if (data.turnId) setActiveTurnId(data.turnId);
        },
        onError: (err: unknown) => {
          removeLastUserMessage(content);
          // TURN_IN_PROGRESS: another turn is already running (post-stop race). Restore the prompt
          // so the user can retry once the abort completes; reconnect to the active turn's stream.
          const errData = (err as { data?: { code?: string; activeTurnId?: string } })?.data;
          if (errData?.code === 'TURN_IN_PROGRESS' && errData.activeTurnId) {
            setPrefillValue(content);
            setPrefillNonce((n) => n + 1);
            setActiveTurnId(errData.activeTurnId);
            return;
          }
          const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
          addMessage({ type: 'error', message: msg });
          setAgentActive(false);
          setActiveTurnId(null);
        },
      });
    },
    [sessionId, store, turnStop.blocking, addMessage, removeLastUserMessage, sendMessageMutation],
  );

  // ── Reset ──
  const resetMutation = useMutation(orpc.routineChat.reset.mutationOptions());

  const resetChat = useCallback(() => {
    if (!sessionKey) return;
    resetMutation.mutate(
      { workflowId: sessionKey },
      {
        onSuccess: () => {
          setSessionId(null);
          clearMessages();
          initializedForSession.current = null;
          setAgentActive(false);
          setActiveTurnId(null);
          void refetchSession();
        },
      },
    );
  }, [sessionKey, resetMutation, clearMessages, refetchSession]);

  const sessionReady = !!sessionId;

  return {
    sessionId,
    messages,
    connected,
    error: streamHistoryRecovered ? null : sseError,
    sendMessage,
    resetChat,
    agentActive: agentActive || turnStop.blocking,
    stopping: turnStop.stopping || turnStop.pending,
    stopStatusError: turnStop.statusError,
    stopStalled: turnStop.stopStalled,
    retryStopStatus: () => {
      void turnStop.retryStatus();
    },
    sessionReady,
    stopTurn,
    canStop:
      (agentActive || turnStop.blocking) &&
      (knownTurnId !== null || turnStop.blockingTurnId !== null) &&
      !turnStop.terminalSeen &&
      !turnStop.stopping &&
      !turnStop.pending,
    prefillValue,
    prefillNonce,
  };
}
